/* eslint-disable react/no-danger */
import React from "react";
import ComponentMapRender from '@lmig/dotcom-aspect-components/Utility/ComponentMapRender';
import ArticleWithImage from '@lmig/dotcom-aspect-components/ArticleWithImage';
import Heading from "@lmig/dotcom-aspect-components/Utility/Heading";
import Paragraph from '@lmig/dotcom-aspect-components/Utility/Paragraph';
import AccordionChildren from '@lmig/dotcom-aspect-components/Utility/AccordionChildren';
import Logger from '@lmig/dotcom-utilities-helpers/logger';
import { titleCase } from "title-case";
import RichText from "@lmig/dotcom-aspect-comparion-components/RichText";
import withPreprocessedContent from "@lmig/dotcom-aspect-comparion-components/Utility/withPreprocessedContent";
import Layout from "@lmig/dotcom-aspect-comparion-components/Layout";
import Head from "@lmig/dotcom-aspect-comparion-components/Head";
import BreadcrumbSticky from "@lmig/dotcom-aspect-comparion-components/BreadcrumbSticky";
import OfficeHero from "@lmig/dotcom-aspect-comparion-components/Office/OfficeHero";
import LazyLoad from "@lmig/dotcom-aspect-comparion-components/LazyLoad";
import FooterBanner from "@lmig/dotcom-aspect-comparion-components/FooterBanner";
import OfficeAbout from "@lmig/dotcom-aspect-comparion-components/Office/OfficeAbout";
import Reviews from "@lmig/dotcom-aspect-comparion-components/Reviews";
import sortReviews from "@lmig/dotcom-aspect-comparion-helpers/sortReviews";
import { emsGraphQlQuery } from "@lmig/dotcom-aspect-comparion-helpers/emsGraphQlQuery";
import { filterUndefined } from "@lmig/dotcom-aspect-comparion-helpers/filterUndefined";
import { getServerSideProps as callGetServerSideProps } from "@lmig/dotcom-aspect-comparion-helpers/staticPageController";
import { formatOffice, formatAgent } from "@lmig/dotcom-aspect-comparion-helpers/formatEntity";
import WrapperSizeable from "@lmig/dotcom-aspect-comparion-components/Utility/WrapperSizeable";
import ReviewsSchema from "@lmig/dotcom-aspect-comparion-components/Utility/ReviewsSchema";
import InsurancePartners from "@lmig/dotcom-aspect-comparion-components/InsurancePartners";
import OfficeLicensedIn from "@lmig/dotcom-aspect-comparion-components/Office/OfficeLicensedIn";
import OfficeMap from "@lmig/dotcom-aspect-comparion-components/Office/OfficeMap";
import OfficeAgents from "@lmig/dotcom-aspect-comparion-components/Office/OfficeAgents";
import InsuranceProducts from "@lmig/dotcom-aspect-comparion-components/InsuranceProducts";

const logger = new Logger('client:layout:office-landing');

const OfficeLanding = withPreprocessedContent(({ content: { mainContent = [], meta = {} }, office, agentLicenses, agents, reviews, breadcrumb }) => {
  const componentMap = {
    accordion: AccordionChildren,
    articleWithImage: ArticleWithImage,
    heading: Heading,
    insurancePartners: ({ ...rest }) => <LazyLoad><InsurancePartners {...rest} /></LazyLoad>,
    insuranceProducts: ({ ...rest }) => <LazyLoad><InsuranceProducts isRemote={false} city={office.address.city} state={office.address.state.code}{...rest} /></LazyLoad>,
    meetOurAgents: () => <LazyLoad><OfficeAgents agents={agents} city={office.address.city} state={office.address.state.code} hasCity /></LazyLoad>,
    officeAbout: ({ ...rest }) => <OfficeAbout office={office} {...rest} />,
    officeHero: () => <OfficeHero office={office} />,
    officeLicensedIn: () => <LazyLoad><OfficeLicensedIn licenses={agentLicenses} /></LazyLoad>,
    officeMapInformational: () => <LazyLoad><OfficeMap office={office} /></LazyLoad>,
    paragraph: Paragraph,
    reviews: () => <Reviews {...reviews} googleWriteReview={office.googlePlaceId} comparionWriteReview={office.firstPartyReviewPage} name={`${office.address.city} ${office.address.state.code}`} />,
    richText: RichText,
    wrapperSizeable: WrapperSizeable,
  };

  const officePhone = office.phones?.primary?.number || office.phones?.toll?.number;

  return (
    <>
      <Head {...meta} />
      <Layout agentSession={false}>
        <BreadcrumbSticky backgroundColor="#f5f5f5" breadcrumbList={breadcrumb} />
        <FooterBanner phoneNumber={officePhone} />
        <div itemProp="mainEntity" itemScope itemType="https://schema.org/InsuranceAgency">
          <meta itemProp="name" content={`${office.address.city}, ${office.address.state.code}`} />
          <meta itemProp="brand" content="Comparion Insurance" />
          <ReviewsSchema reviews={reviews?.allReviews} totalRating={reviews?.totalRating} />
          <ComponentMapRender content={mainContent} componentMap={componentMap} />
        </div>
      </Layout>
    </>
  );
});

// eslint-disable-next-line complexity
const getServerSideProps = async (context) => {
  const { req: { params: { alias, officeState } }, res } = context;
  const resultProps = await callGetServerSideProps(context);

  if (resultProps.notFound) {
    return resultProps;
  }

  const officeStateProper = titleCase(officeState.replace('-', ' '));

  const rez = await emsGraphQlQuery({
    cacheKeyPrefix: `officeLanding-alias-${alias}-office`,
    params: {
      query: `query {
        officeLanding:OfficeOne(filter:{officeType:FIELD,alias:"${alias}",address:{state:{name:"${officeStateProper}"}}}){
          active
          isRemote
          name
          address{street additionalStreetInfo city zip state{name code}}
          hours
          phones{number type callCenter}
          location{coordinates}
          image{src alt height width}
          officeCode
          description
          dailyHours{
            monday{isClosed eveningsByAppointment byAppointmentOnly start end}
            tuesday{isClosed eveningsByAppointment byAppointmentOnly start end}
            wednesday{isClosed eveningsByAppointment byAppointmentOnly start end}
            thursday{isClosed eveningsByAppointment byAppointmentOnly start end}
            friday{isClosed eveningsByAppointment byAppointmentOnly start end}
            saturday{isClosed eveningsByAppointment byAppointmentOnly start end}
            sunday{isClosed eveningsByAppointment byAppointmentOnly start end}
          }
          reviews{
            id status rating content authorName publisherId publisherDate reviewLanguage comments{
              content visibility authorName authorEmail authorRole publisherDate
            }
          }
          rating
          googlePlaceId
          firstPartyReviewPage
          videoUrl
          quote
          reservationUrl
          social{
            instagram
            facebook
            linkedin
            twitter
          }
        }
      }`}
  });
  const { data: { officeLanding: office = null } } = rez;

  if (office === null) {
    logger.debug('Office not found, redirecting to search page');
    res.redirect(301, `/insurance-agent`);
    res.end();

    return { props: {} };
  }

  if (!office.active) {
    logger.debug(`Expired office, redirecting to state page`);
    res.redirect(301, `/insurance-agent/${officeState}`);
    res.end();

    return resultProps;
  }

  const { data: { officeLandingAgents: agents = [] } } = await emsGraphQlQuery({
    cacheKeyPrefix: `officeLanding-alias-${alias}-reviews`,
    params: {
      query: `query {
        officeLandingAgents:AgentMany(limit:500,filter:{type:FIELD,active:true,offices:{alias:"${alias}"}}){
          displayName
          firstName
          preferredName
          yextTitle
          title
          email
          phones{type number extension}
          qualifications{title}
          image{src alt}
          nNumber
          aliasPath
          rating
          reviews{id rating}
          reviewCount
          profile {completeness fields{image}}
          licenseData{
            ak{stateName licenses{type number}}
            al{stateName licenses{type number}}
            ar{stateName licenses{type number}}
            az{stateName licenses{type number}}
            ca{stateName licenses{type number}}
            co{stateName licenses{type number}}
            ct{stateName licenses{type number}}
            dc{stateName licenses{type number}}
            de{stateName licenses{type number}}
            fl{stateName licenses{type number}}
            ga{stateName licenses{type number}}
            hi{stateName licenses{type number}}
            ia{stateName licenses{type number}}
            id{stateName licenses{type number}}
            il{stateName licenses{type number}}
            in{stateName licenses{type number}}
            ks{stateName licenses{type number}}
            ky{stateName licenses{type number}}
            la{stateName licenses{type number}}
            ma{stateName licenses{type number}}
            md{stateName licenses{type number}}
            me{stateName licenses{type number}}
            mi{stateName licenses{type number}}
            mn{stateName licenses{type number}}
            mo{stateName licenses{type number}}
            ms{stateName licenses{type number}}
            mt{stateName licenses{type number}}
            nc{stateName licenses{type number}}
            nd{stateName licenses{type number}}
            ne{stateName licenses{type number}}
            nh{stateName licenses{type number}}
            nj{stateName licenses{type number}}
            nm{stateName licenses{type number}}
            nv{stateName licenses{type number}}
            ny{stateName licenses{type number}}
            oh{stateName licenses{type number}}
            ok{stateName licenses{type number}}
            or{stateName licenses{type number}}
            pa{stateName licenses{type number}}
            pr{stateName licenses{type number}}
            ri{stateName licenses{type number}}
            sc{stateName licenses{type number}}
            sd{stateName licenses{type number}}
            tn{stateName licenses{type number}}
            tx{stateName licenses{type number}}
            ut{stateName licenses{type number}}
            va{stateName licenses{type number}}
            vt{stateName licenses{type number}}
            wa{stateName licenses{type number}}
            wi{stateName licenses{type number}}
            wv{stateName licenses{type number}}
            wy{stateName licenses{type number}}
          }
        }
      }` }
  });

  const formattedOffice = formatOffice(office);
  const officeCodes = [
    "0186",
    "083E",
    "0106",
    "0671",
    "0065",
    "0240",
    "0944",
    "0943",
    "0380",
    "0956",
    "0554",
    "024D",
    "093A",
    "0134",
    "019D",
    "032P",
    "0334",
    "0111",
    "0407",
    "0286",
    "007P",
    "0614",
    "041J",
    "085H",
    "072D",
    "0611",
    "0825",
    "0423",
    "0138",
    "0372",
    "010D",
    "0643",
    "048C",
    "0278",
    "0506",
    "0822",
    "0357",
    "0880",
    "0317",
    "0575",
    "0465",
    "0586",
    "0568",
    "0555",
    "0594",
    "0326",
    "0807",
    "0851",
    "106S",
    "035D",
    "065P",
    "0280",
    "095E",
    "0942",
    "0948",
    "0360",
    "0936",
    "0493",
    "0885",
    "073D",
    "0565",
    "0346",
    "0672",
    "011P",
    "0166",
    "0103",
    "049C",
    "0875",
    "0536",
    "0894",
    "0382",
    "0173",
    "0126",
    "0356",
    "0544",
    "0569",
    "0474",
    "046C",
    "021E",
    "0221",
    "0529",
    "0933",
    "0869",
    "0630",
    "0645",
    "0533",
    "0517",
    "0843",
    "0537",
    "0578",
    "0660",
    "0488",
    "0512",
    "0457",
    "0600",
    "0937",
    "059F",
    "0304",
    "016P",
    "0192",
    "080C",
  ];

  if (officeCodes.includes(formattedOffice.officeCode)) {
    formattedOffice.firstPartyReviewPage = null;
  }

  resultProps.props.office = filterUndefined(formattedOffice);

  try {
    const currentPageJson = JSON.parse(context.query.currentPage);
    currentPageJson.breadcrumb[3].name = office.address.city;
    resultProps.props.breadcrumb = currentPageJson.breadcrumb;
  } catch (e) {
    logger.error(`Error encountered updating breadcrumb with message: ${e.message}`);
  }

  const licenses = [];
  const formattedAgents = [];

  await Promise.all(agents.map(async agent => {
    const formattedAgent = filterUndefined(formatAgent(agent));
    formattedAgents.push(formattedAgent);

    if (formattedAgent.licenseData) {
      // eslint-disable-next-line array-callback-return
      await Promise.all(Object.entries(formattedAgent.licenseData).map(([, { stateName }]) => {
        licenses.push(stateName);
      }));
    }
  }));

  resultProps.props.agents = formattedAgents;
  resultProps.props.agentLicenses = Array.from(new Set(licenses)).sort();
  resultProps.props.reviews = sortReviews(office.reviews, office.rating);

  resultProps.props.content.meta.description = `Expert insurance agents in ${office.address.city}, ${office.address.state.code} ready to get you free insurance quotes from multiple carriers.`;
  resultProps.props.content.meta.title = `${office.address.city}, ${office.address.state.code} Insurance Agents | Comparion Insurance Agency`;

  return resultProps;
};

export { getServerSideProps };

export default OfficeLanding;
